import { Link, useParams } from "react-router-dom";
import { ArrowRight, MapPin, Ruler, Layers } from "lucide-react";
import { REALISATIONS } from "../data/realisations";
import { getService } from "../data/services";
import { getLocalPage } from "../data/localPages";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { Reveal, Label } from "../components/Reveal";
import { CTASection } from "../components/CTASection";
import NotFound from "./NotFound";

const SITE_URL = "https://resineko.com";

const ChantierPage = () => {
  const { slug } = useParams();
  const chantier = REALISATIONS.find((r) => r.slug === slug);
  if (!chantier) return <NotFound />;

  const service = chantier.serviceSlug ? getService(chantier.serviceSlug) : null;
  const localPage = chantier.localSlug ? getLocalPage(chantier.localSlug) : null;
  const title = chantier.pageTitle || `${chantier.technique} — ${chantier.espace} à ${chantier.ville}`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: title,
        image: `${SITE_URL}${chantier.image}`,
        description: chantier.description,
        author: { "@type": "Organization", name: "Resineko", url: SITE_URL },
        about: { "@type": "Service", name: chantier.technique, provider: { "@type": "LocalBusiness", name: "Resineko", telephone: "+33783693101" } },
        contentLocation: { "@type": "City", name: chantier.ville },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: SITE_URL },
          { "@type": "ListItem", position: 2, name: "Réalisations", item: `${SITE_URL}/realisations` },
          { "@type": "ListItem", position: 3, name: title, item: `${SITE_URL}/realisations/${chantier.slug}` },
        ],
      },
    ],
  };

  return (
    <>
      <Seo
        title={`${title} | Resineko`}
        description={chantier.description}
        path={`/realisations/${chantier.slug}`}
        jsonLd={jsonLd}
      />
      <PageHero
        kicker={`Chantier — ${chantier.ville}`}
        title={title}
        subtitle={chantier.description}
        image={chantier.image}
        dim
      />

      {/* Récit du chantier */}
      <section className="noise-bg bg-[#0A0A0A] py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 md:grid-cols-12 md:px-10">
          <div className="md:col-span-7">
            <Reveal><Label>Le chantier</Label></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif text-4xl font-light leading-[1.05] text-[#F4F4F0] md:text-5xl">
                Comment nous avons mené ce projet
              </h2>
            </Reveal>
            <div className="mt-10 space-y-6">
              {chantier.story.map((p, i) => (
                <Reveal key={i} delay={0.15 + i * 0.05}>
                  <p className="text-sm leading-relaxed text-[#8C8C8C] md:text-base">{p}</p>
                </Reveal>
              ))}
            </div>
          </div>
          <div className="md:col-span-4 md:col-start-9">
            <Reveal delay={0.2}>
              <div className="border border-white/10 bg-[#111] p-8" data-testid="chantier-infos">
                <p className="text-[11px] uppercase tracking-[0.3em] text-[#8C8C8C]">Fiche chantier</p>
                <div className="mt-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <Layers size={16} className="mt-0.5 shrink-0 text-[#2FBF8F]" />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C8C8C]">Technique</p>
                      <p className="mt-1 text-sm text-[#F4F4F0]">{chantier.technique}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={16} className="mt-0.5 shrink-0 text-[#2FBF8F]" />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C8C8C]">Lieu</p>
                      <p className="mt-1 text-sm text-[#F4F4F0]">{chantier.ville}</p>
                    </div>
                  </div>
                  {chantier.surface && (
                    <div className="flex items-start gap-3">
                      <Ruler size={16} className="mt-0.5 shrink-0 text-[#2FBF8F]" />
                      <div>
                        <p className="text-[10px] uppercase tracking-[0.2em] text-[#8C8C8C]">Surface</p>
                        <p className="mt-1 text-sm text-[#F4F4F0]">{chantier.surface}</p>
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-8 border-t border-white/10 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-[#2FBF8F]">Réalisation Resineko</p>
                  <p className="mt-2 text-xs leading-relaxed text-[#8C8C8C]">
                    Chantier réel réalisé par nos soins. Chaque projet est unique : le vôtre recevra la même exigence.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Photos du chantier (si plusieurs) */}
      {chantier.photos && chantier.photos.length > 1 && (
        <section className="border-t border-white/10 bg-[#0e0e0e] py-16 md:py-24" data-testid="chantier-photos">
          <div className="mx-auto max-w-7xl px-5 md:px-10">
            <Reveal><Label>Le chantier en images</Label></Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {chantier.photos.map((src, i) => (
                <Reveal key={src} delay={i * 0.06}>
                  <div className="overflow-hidden border border-white/10">
                    <img src={src} alt={`${title} — photo ${i + 1}`} loading="lazy" className="aspect-[4/3] w-full object-cover" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Liens internes */}
      <section className="border-t border-white/10 bg-[#0e0e0e] py-14">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-5 md:px-10">
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#8C8C8C]">Pour aller plus loin</span>
          {service && (
            <Link to={`/${service.slug}`} data-testid="chantier-link-service" className="border border-[#047857]/50 bg-[#047857]/10 px-4 py-2 text-xs text-[#F4F4F0] transition-colors hover:border-[#2FBF8F]">
              Découvrir : {service.name}
            </Link>
          )}
          {localPage && (
            <Link to={`/${localPage.slug}`} data-testid="chantier-link-local" className="border border-[#047857]/50 bg-[#047857]/10 px-4 py-2 text-xs text-[#F4F4F0] transition-colors hover:border-[#2FBF8F]">
              Nos interventions à {localPage.city}
            </Link>
          )}
          <Link to="/realisations" data-testid="chantier-link-realisations" className="border border-white/15 px-4 py-2 text-xs text-[#F4F4F0]/75 transition-colors hover:border-[#047857] hover:text-[#2FBF8F]">
            Toutes nos réalisations
          </Link>
        </div>
      </section>

      <CTASection
        title={`Un projet similaire à ${chantier.ville} ?`}
        text="Décrivez-nous votre espace : nous vous orientons vers la technique et la finition adaptées, avec la même exigence que sur ce chantier."
      />
    </>
  );
};

export default ChantierPage;
